import React, { useState, useEffect } from "react";
import menuData from "../data/menuDataLocations";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/Menu.css";
import MenuNavbar from "../components/MenuNavbar";

function MenuStyled() {
	const [selectedStore, setSelectedStore] = useState(menuData.stores[0].id);
	const [selectedSection, setSelectedSection] = useState("");
	const [isCategoryOpen, setIsCategoryOpen] = useState(false);

	useEffect(() => {
		setSelectedSection("");
	}, [selectedStore]);

	const currentMenu =
		menuData.menus[selectedStore] || menuData.menus[menuData.stores[0].id];
	const sections = currentMenu.map((section) => section.section);
	const filteredMenuData = selectedSection
		? currentMenu.filter((section) => section.section === selectedSection)
		: currentMenu;

	const handleDownload = () => {
		try {
			const link = document.createElement("a");
			link.href = "menu.pdf";
			link.download = "menu.pdf";
			link.click();
		} catch (error) {
			console.error("Failed to download the menu:", error);
		}
	};

	return (
		<div className="min-h-screen w-full bg-white">
			{/* Mobile store selector + Explore Categories button */}
			<div className="block md:hidden mt-12 px-4 sm:px-8 pb-2">
				<div className="flex items-center justify-between gap-3">
					<div className="flex flex-wrap gap-3">
						{menuData.stores.map((store) => (
							<button
								key={`mobile-${store.id}`}
								type="button"
								onClick={() => setSelectedStore(store.id)}
								className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
									selectedStore === store.id
										? "bg-black text-white"
										: "bg-gray-100 text-gray-700 hover:bg-gray-200"
								}`}
							>
								{store.label}
							</button>
						))}
					</div>
					<div>
						<button
							onClick={() => setIsCategoryOpen(!isCategoryOpen)}
							className="ml-2 px-3 py-2 bg-gray-100 rounded-full text-sm text-[#856746] font-medium border border-gray-200"
							aria-label="Explore categories"
						>
							Categories
						</button>
					</div>
				</div>
				<div className="text-center text-sm text-gray-600 mt-1 mb-4">
					Showing the{" "}
					{menuData.stores.find((store) => store.id === selectedStore)?.label}{" "}
					menu.
				</div>
			</div>

			{/* Menu Navigation Bar */}
			<MenuNavbar
				sections={sections}
				selectedSection={selectedSection}
				setSelectedSection={setSelectedSection}
				isMobileOpen={isCategoryOpen}
				setIsMobileOpen={setIsCategoryOpen}
			/>

			{/* Desktop store selector */}
			<div className="hidden md:flex mt-6 flex-wrap justify-center gap-3 px-4 sm:px-8">
				{menuData.stores.map((store) => (
					<button
						key={store.id}
						type="button"
						onClick={() => setSelectedStore(store.id)}
						className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
							selectedStore === store.id
								? "bg-black text-white"
								: "bg-gray-100 text-gray-700 hover:bg-gray-200"
						}`}
					>
						{store.label}
					</button>
				))}
			</div>
			<div className="hidden md:block text-center text-sm text-gray-600 mt-2 mb-8">
				Showing the{" "}
				{menuData.stores.find((store) => store.id === selectedStore)?.label}{" "}
				menu.
			</div>

			<div className="relative pt-6 md:pt-32 px-4 sm:px-8 pb-20">
				{/* Menu Content */}
				<div className="pt-8 md:pt-20">
					<div className="max-w-4xl mx-auto">
						{filteredMenuData.map((section, idx) => (
							<section key={idx} className="mb-12 md:mb-28">
								<div className="bg-white p-3 md:p-8">
									<h2 className="text-lg md:text-3xl font-bold text-center mb-4 md:mb-6 tracking-wider text-[#2C2C2C] uppercase">
										{section.section}
									</h2>

									{/* Two lines with dot decoration */}
									<div className="flex justify-center items-center gap-1 mb-6 md:mb-16">
										<div className="w-6 md:w-16 h-0.5 bg-[#8B4513]"></div>
										<div className="w-1 h-1 rounded-full bg-[#8B4513]"></div>
										<div className="w-6 md:w-16 h-0.5 bg-[#8B4513]"></div>
									</div>

									<div className="space-y-3 md:space-y-6">
										{section.items.map((item, i) => (
											<div
												key={i}
												className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 md:gap-3 hover:bg-gray-50 p-3 rounded-lg transition-colors duration-200"
											>
												<div className="flex-1">
													<h3 className="font-bold text-sm md:text-xl tracking-wide text-black mb-1">
														{item.title}
													</h3>
													{item.description && (
														<p className="text-gray-600 text-[10px] md:text-sm font-light leading-relaxed max-w-2xl">
															{item.description}
														</p>
													)}
												</div>
												{item.price && (
													<p className="font-bold text-sm md:text-xl text-black whitespace-nowrap ml-2">
														{item.price}
													</p>
												)}
											</div>
										))}
									</div>
								</div>
							</section>
						))}

						<div className="mt-12 sm:mt-20 pt-8 border-t border-gray-200">
							<p className="text-xs sm:text-sm text-center text-gray-600 font-light italic leading-relaxed">
								Please inform staff of any allergy or dietary requirements.
								<br />
								Weekend Surcharge 10% and Public Holiday 15% Surcharge applies.
							</p>
						</div>
					</div>
				</div>

				{/* Download Button with enhanced styling */}
				<button
					onClick={handleDownload}
					className="fixed bottom-3 sm:bottom-6 right-3 sm:right-6 z-50 bg-black text-white px-3 sm:px-6 py-2 sm:py-3 rounded-full shadow-lg hover:bg-gray-800 hover:scale-105 transition-all duration-300 flex items-center gap-2 group text-xs sm:text-base"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						className="h-4 w-4 sm:h-5 sm:w-5 transform group-hover:translate-y-0.5 transition-transform duration-300"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3"
						/>
					</svg>
					<span>Download Menu</span>
				</button>
			</div>
		</div>
	);
}

export default MenuStyled;
